import React, { createContext, useContext, useState, useEffect } from 'react';
import { EndPointGetVehicles, EndPointDeleteVehicle, EndPointCreateUpdateVehicle, EndPointGetVehicleType } from '../Services/EndPointVehicle';

export const VehiclesContext = createContext();

// Custom hook to use the Vehicles context
export const useVehicles = () => {
  const context = useContext(VehiclesContext);
  if (!context) {
    throw new Error('useVehicles must be used within a VehiclesProvider');
  }
  return context;
};

// Provider component
export const VehiclesProvider = ({ children }) => {
  const [miningSite, setMiningSite] = useState(0);
  const [nameFilter, setNameFilter] = useState('');
  const [idFilter, setIdFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState(0);
  const [openSave, setOpenSave] = useState(false);
  const [selected, setSelected] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [deleteItem, setDeleteItem] = useState(null);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const fetchVehicleTypes = async () => {
    try {
      const response = await EndPointGetVehicleType();
      const vehicleTypesData = response.data.data || [];
      setVehicleTypes(vehicleTypesData);
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await EndPointGetVehicles(miningSite, idFilter, nameFilter, typeFilter);
      const vehiclesData = response.data.data || [];
      setVehicles(vehiclesData);
      setSelected(null);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const doDelete = async () => {
    try {
      await EndPointDeleteVehicle(deleteItem.vehicleId, miningSite);
      setSelected(null);
      setVehicles(vehicles.filter((item) => item.vehicleId !== deleteItem.vehicleId));
      setDeleteItem(null);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
    }
  };

  useEffect(() => {
    fetchVehicleTypes();
  }, []);

  useEffect(() => {
    if (miningSite === 0) {
      setVehicles([]);
      return;
    }
    
    fetchVehicles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [miningSite, nameFilter, idFilter, typeFilter]);

  const doSave = async (form) => {
    try {
      const data = {
        ...form,
        miningSiteId: miningSite,
        travelType: "I"
      };
      
      const response = await EndPointCreateUpdateVehicle(data);

      if (!response.data.success) {
        return { success: false, error: response.data.message };
      }
      
      fetchVehicles();
      setOpenSave(true);
      return { success: true };
    } catch (error) {
      console.error('Error saving vehicle:', error);
      return { success: false, error: 'Error saving vehicle' };
    }
  };

  const contextValue = {
    miningSite,
    nameFilter,
    idFilter,
    typeFilter,
    selected,
    vehicles,
    deleteItem,
    openSave,
    vehicleTypes,

    setOpenSave,
    setMiningSite,
    setNameFilter,
    setIdFilter,
    setTypeFilter,
    setSelected,
    setDeleteItem,
    setVehicles,

    doSave,
    doDelete,
  };

  return (
    <VehiclesContext.Provider value={contextValue}>
      {children}
    </VehiclesContext.Provider>
  );
};
